import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useAutoSave } from '../hooks/useAutoSave';
import { fileReviewAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import './FileReviewDetail.css';

const COMPLETION_OPTIONS = ['', 'Yes', 'No', 'Incomplete', 'NA'];
const STATUS_ACTIONS = [
  { value: 'Sent_To_SC', label: 'Send to SC', from: ['Open'] },
  { value: 'Ready_For_LSC', label: 'Send back to reviewer', from: ['Sent_To_SC'] },
  { value: 'Review_Complete', label: 'Mark review complete', from: ['Sent_To_SC', 'Ready_For_LSC'] },
];

function FileReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [itemEdits, setItemEdits] = useState({});
  const [saveTrigger, setSaveTrigger] = useState(0);

  useEffect(() => {
    if (!hasPermission('file_reviews.view')) {
      navigate(tenantPath('/lists/file-reviews'), { replace: true });
      return;
    }
    loadReview();
  }, [id, navigate, tenantPath]);

  const loadReview = async (options = {}) => {
    if (!id) return;
    const skipLoading = options.skipLoading === true;
    try {
      if (!skipLoading) setLoading(true);
      setError(null);
      const res = await fileReviewAPI.getReview(id);
      setReview(res.data?.data ?? null);
      setItemEdits({});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load review');
      setReview(null);
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  const setItemEdit = (itemId, field, value) => {
    setItemEdits((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
    if (hasPermission('file_reviews.update')) setSaveTrigger((p) => p + 1);
  };

  const getItemValue = (item, field) => {
    const edit = itemEdits[item.id];
    if (edit && field in edit) return edit[field];
    return item[field];
  };

  const buildItemBody = (edit) => {
    const body = {};
    if (edit && 'completion' in edit) body.completion = edit.completion || null;
    if (edit && 'reviewer_comment' in edit) body.reviewer_comment = edit.reviewer_comment;
    if (edit && 'sc_comment' in edit) body.sc_comment = edit.sc_comment;
    return body;
  };

  const performSave = useCallback(async () => {
    if (!id || !review) return;
    const edits = Object.keys(itemEdits);
    if (edits.length === 0) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      for (const itemId of edits) {
        const edit = itemEdits[itemId];
        if (!edit || Object.keys(edit).length === 0) continue;
        const body = buildItemBody(edit);
        await fileReviewAPI.updateReviewItem(id, itemId, body);
      }
      setItemEdits({});
      await loadReview({ skipLoading: true });
      setSaveMessage('Saved.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [id, review, itemEdits]);

  useAutoSave(saveTrigger, performSave, { enabled: !!id && hasPermission('file_reviews.update'), delayMs: 500 });

  const hasUnsavedEdits = Object.keys(itemEdits).length > 0;

  const handleStatusAction = async (newStatus) => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await fileReviewAPI.updateReview(id, { status: newStatus });
      await loadReview();
      setSaveMessage(res.data?.warning || 'Status updated.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => (d ? formatDisplayDate(d) : '—');
  const clientName = review ? [review.client_last_name, review.client_first_name].filter(Boolean).join(', ') || '—' : '';
  const revieweeName = review ? [review.reviewee_last_name, review.reviewee_first_name].filter(Boolean).join(', ') || '—' : '';
  const reviewerName = review ? [review.reviewer_last_name, review.reviewer_first_name].filter(Boolean).join(', ') || '—' : '';
  const availableStatusActions = review ? STATUS_ACTIONS.filter((a) => a.from.includes(review.status)) : [];

  if (loading && !review) {
    return (
      <div className="file-review-detail-page">
        <div className="file-review-detail-header">
          <div>
            <h1>File review</h1>
          </div>
        </div>
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  if (error && !review) {
    return (
      <div className="file-review-detail-page">
        <div className="file-review-detail-header">
          <div>
            <h1>File review</h1>
          </div>
        </div>
        <p className="error-msg">{error}</p>
      </div>
    );
  }

  if (!review) return null;

  const canUpdate = hasPermission('file_reviews.update');

  return (
    <div className="file-review-detail-page">
      <div className="file-review-detail-header">
        <div>
          <h1>File review</h1>
        </div>
      </div>

      {saveMessage && <p className={saveMessage.startsWith('Failed') ? 'error-msg' : 'save-msg'}>{saveMessage}</p>}

      <section className="file-review-meta">
        <div className="meta-grid">
          <div className="meta-item">
            <span className="meta-label">Client</span>
            <Link to={tenantPath(`/client/${review.client_id}`)}>{clientName}</Link>
          </div>
          <div className="meta-item">
            <span className="meta-label">Review type</span>
            <span>{review.review_type}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Status</span>
            <span>{review.status === 'Review_Complete' ? 'Complete' : (review.status?.replace(/_/g, ' ') || '—')}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Reviewee (SC)</span>
            <span>{revieweeName}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Reviewer</span>
            <span>{reviewerName}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Review date</span>
            <span>{formatDate(review.review_date)}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">SC due</span>
            <span>{formatDate(review.sc_due_date)}</span>
          </div>
        </div>
        {hasPermission('file_reviews.update') && availableStatusActions.length > 0 && (
          <div className="status-actions">
            {availableStatusActions.map((a) => (
              <button
                key={a.value}
                type="button"
                className="btn-status-action"
                onClick={() => handleStatusAction(a.value)}
                disabled={saving}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="file-review-items">
        <h2>Review items</h2>
        <div className="items-table-wrap">
          <table className="items-table">
            <thead>
              <tr>
                <th className="col-question">Question</th>
                <th className="col-completion">Completion</th>
                <th className="col-reviewer">Reviewer note</th>
                <th className="col-sc">SC comment</th>
              </tr>
            </thead>
            <tbody>
              {(review.items || []).map((item) => (
                item.is_section_title ? (
                  <tr key={item.id} className="item-row-section">
                    <td colSpan="4" className="item-section-title">{item.question_text}</td>
                  </tr>
                ) : (
                  <tr key={item.id} className="item-row">
                    <td className="col-question">{item.question_text}</td>
                    <td className="col-completion">
                      <select
                        value={getItemValue(item, 'completion') ?? item.completion ?? ''}
                        onChange={(e) => setItemEdit(item.id, 'completion', e.target.value || null)}
                        disabled={!canUpdate}
                        className="input-completion"
                        title="Completion"
                        aria-label="Completion"
                      >
                        {COMPLETION_OPTIONS.map((opt) => (
                          <option key={opt || 'blank'} value={opt}>{opt || '—'}</option>
                        ))}
                      </select>
                    </td>
                    <td className="col-reviewer">
                      <input
                        type="text"
                        value={getItemValue(item, 'reviewer_comment') ?? item.reviewer_comment ?? ''}
                        onChange={(e) => setItemEdit(item.id, 'reviewer_comment', e.target.value)}
                        disabled={!canUpdate}
                        className="input-comment"
                        placeholder="Reviewer note"
                        title="Reviewer note"
                      />
                    </td>
                    <td className="col-sc">
                      <input
                        type="text"
                        value={getItemValue(item, 'sc_comment') ?? item.sc_comment ?? ''}
                        onChange={(e) => setItemEdit(item.id, 'sc_comment', e.target.value)}
                        disabled={!canUpdate}
                        className="input-comment"
                        placeholder="SC comment"
                        title="SC comment"
                      />
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
        {canUpdate && (
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(tenantPath('/lists/file-reviews'))}
              disabled={saving}
              className="btn-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => performSave()}
              disabled={saving || !hasUnsavedEdits}
              className="btn-save"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

export default FileReviewDetail;
